import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  ShoppingBag,
  CalendarDays,
  IndianRupee,
  Hourglass,
  Lock,
  Bell,
  BellOff,
  Printer,
  TrendingUp,
  ChefHat,
  UtensilsCrossed,
} from "lucide-react";
import MenuManager from "@/components/MenuManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  preparing: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  ready: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  completed: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
};

const RESTAURANT = {
  name: "PODI SAMBARAM",
  tagline: "Taste the Tradition — Authentic South Indian Food",
  address: "Shop 14, Commercial Block, Global City Center, Global Heights, Sector 33, Sohna, Gurugram 122103",
  phone: "+91 92176 12215",
  fssai: "FSSAI Lic. No. 20826005006580",
  upi: "yespay.mabs0716619ikit2268@yesbankltd",
};

const zoneLabel = (o) => {
  if (o.order_type === "dine_in") return `Dine-In${o.table_number ? ` • Table ${o.table_number}` : ""}`;
  if (o.delivery_zone === "beyond_3km") return "Delivery • Beyond 3 KM";
  if (o.delivery_zone === "within_3km") return "Delivery • Within 3 KM";
  return "Takeaway / Pickup";
};

const formatDetail = (detail) => {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

const fmtDay = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", weekday: "short" });

const Receipt = ({ order }) =>
  createPortal(
    <div id="print-receipt" data-testid="print-receipt">
      <div style={{ maxWidth: 300, margin: "0 auto", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#000" }}>
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>{RESTAURANT.name}</p>
          <p style={{ fontSize: 10 }}>{RESTAURANT.tagline}</p>
          <p style={{ fontSize: 10, marginTop: 4 }}>{RESTAURANT.address}</p>
          <p style={{ fontSize: 10 }}>{RESTAURANT.phone} • {RESTAURANT.fssai}</p>
        </div>
        <hr style={{ borderTop: "1px dashed #000", margin: "10px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
          <span style={{ fontWeight: 700 }}>Order {order.order_no}</span>
          <span>{fmtTime(order.created_at)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 2 }}>
          <span>{order.customer_name} • {order.phone}</span>
          <span>{zoneLabel(order)}</span>
        </div>
        <hr style={{ borderTop: "1px dashed #000", margin: "10px 0" }} />
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
          <tbody>
            {order.items.map((i) => (
              <tr key={i.name}>
                <td style={{ padding: "2px 0" }}>{i.qty} × {i.name}</td>
                <td style={{ textAlign: "right", padding: "2px 0" }}>₹{i.price * i.qty}</td>
              </tr>
            ))}
            {order.delivery_fee > 0 && (
              <tr>
                <td style={{ padding: "2px 0" }}>Delivery fee (beyond 3 KM)</td>
                <td style={{ textAlign: "right", padding: "2px 0" }}>₹{order.delivery_fee}</td>
              </tr>
            )}
          </tbody>
        </table>
        <hr style={{ borderTop: "1px dashed #000", margin: "10px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700 }}>
          <span>TOTAL</span>
          <span>₹{order.total}</span>
        </div>
        <p style={{ fontSize: 11, marginTop: 4 }}>
          Payment: {order.payment_status === "paid" ? "PAID (UPI)" : "PENDING — scan below to pay"}
        </p>
        {order.notes && <p style={{ fontSize: 10, marginTop: 2 }}>Notes: {order.notes}</p>}
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <img src="/assets/payment-qr.png" alt="UPI QR" style={{ width: 150, margin: "0 auto" }} />
          <p style={{ fontSize: 9, marginTop: 4 }}>UPI: {RESTAURANT.upi}</p>
        </div>
        <hr style={{ borderTop: "1px dashed #000", margin: "10px 0" }} />
        <p style={{ textAlign: "center", fontSize: 11 }}>Thank you! Taste the Tradition.</p>
      </div>
    </div>,
    document.body
  );

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem("podi_admin_token") || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!token);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("orders");
  const [filter, setFilter] = useState("all");
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [printOrder, setPrintOrder] = useState(null);
  const [soundOn, setSoundOn] = useState(localStorage.getItem("podi_admin_sound") !== "off");
  const audioRef = useRef(null);
  const knownIdsRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const logout = useCallback(() => {
    localStorage.removeItem("podi_admin_token");
    setToken("");
    setUser(null);
    knownIdsRef.current = null;
  }, []);

  const playChime = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      [880, 1174.66, 880].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const t0 = now + i * 0.18;
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.4);
      });
    } catch (e) {
      // audio not available
    }
  }, []);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("podi_admin_sound", next ? "on" : "off");
    if (next) playChime();
  };

  const fetchMenu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/menu`);
      setMenuCategories(res.data.categories.map((c) => c.title));
      setMenuItems(
        res.data.categories.flatMap((c) =>
          c.items.map((i) => ({ ...i, cat: c.title }))
        )
      );
    } catch (e) {
      console.error("menu fetch failed", e);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [o, r] = await Promise.all([
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/reservations`, { headers }),
      ]);
      const prev = knownIdsRef.current;
      if (prev === null) {
        knownIdsRef.current = new Set(o.data.map((x) => x.id));
      } else {
        const fresh = o.data.filter((x) => !prev.has(x.id));
        if (fresh.length > 0) {
          fresh.forEach((x) => prev.add(x.id));
          playChime();
          const f = fresh[0];
          toast.success(`New order ${f.order_no} — ₹${f.total}`, {
            description: `${f.customer_name} • ${f.order_type === "dine_in" ? "Dine-In" : "Takeaway"}`,
          });
        }
      }
      setOrders(o.data);
      setReservations(r.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, logout, playChime]);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers })
      .then((res) => {
        setUser(res.data);
        fetchAll();
        fetchMenu();
      })
      .catch(() => logout())
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!user) return;
    const t = setInterval(fetchAll, 20000);
    return () => clearInterval(t);
  }, [user, fetchAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem("podi_admin_token", res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
    } catch (err) {
      setLoginError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const markPaid = async (order) => {
    try {
      await axios.patch(`${API}/orders/${order.id}/payment`, {}, { headers });
      toast.success(`Payment confirmed for ${order.order_no}`);
      fetchAll();
    } catch {
      toast.error("Could not update payment");
    }
  };

  const setStatus = async (order, status) => {
    try {
      await axios.patch(`${API}/orders/${order.id}/status`, { status }, { headers });
      fetchAll();
    } catch {
      toast.error("Could not update status");
    }
  };

  const printBill = (order) => {
    setPrintOrder(order);
    setTimeout(() => window.print(), 200);
  };

  const sales = useMemo(() => {
    const valid = orders.filter((o) => o.status !== "cancelled");
    const itemMap = {};
    valid.forEach((o) =>
      o.items.forEach((i) => {
        if (!itemMap[i.name]) itemMap[i.name] = { name: i.name, qty: 0, revenue: 0 };
        itemMap[i.name].qty += i.qty;
        itemMap[i.name].revenue += i.qty * i.price;
      })
    );
    const items = Object.values(itemMap).sort((a, b) => b.qty - a.qty);
    const byDay = {};
    valid.forEach((o) => {
      const day = new Date(o.created_at).toDateString();
      if (!byDay[day]) byDay[day] = { day, orders: 0, paidOrders: 0, revenue: 0, fees: 0 };
      byDay[day].orders += 1;
      if (o.payment_status === "paid") {
        byDay[day].paidOrders += 1;
        byDay[day].revenue += o.total;
        byDay[day].fees += o.delivery_fee || 0;
      }
    });
    const days = Object.values(byDay)
      .sort((a, b) => new Date(b.day) - new Date(a.day))
      .slice(0, 7);
    return { items, days };
  }, [orders]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0C] text-zinc-400">
        Checking session...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0C] px-5">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="card-surface w-full max-w-sm rounded-3xl p-8"
        >
          <div className="flex flex-col items-center text-center">
            <img src="/assets/mark.png" alt="Podi Sambaram" className="h-16 w-16 rounded-full object-cover" />
            <h1 className="mt-4 font-display text-2xl font-semibold text-[#F5F2EB]">Staff Login</h1>
            <p className="mt-1 text-xs text-zinc-500">Podi Sambaram — Admin Dashboard</p>
          </div>
          <input
            data-testid="admin-login-email-input"
            type="email"
            required
            value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            placeholder="Email"
            className="mt-6 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-[#F57C00]/60"
          />
          <input
            data-testid="admin-login-password-input"
            type="password"
            required
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            placeholder="Password"
            className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-[#F57C00]/60"
          />
          {loginError && (
            <p data-testid="admin-login-error" className="mt-3 text-center text-xs text-red-400">
              {loginError}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loggingIn}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#F57C00] py-3 font-grotesk text-sm font-semibold text-black transition-all hover:bg-[#FF9800] disabled:opacity-60"
          >
            <Lock size={14} /> {loggingIn ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const today = new Date().toDateString();
  const todaysOrders = orders.filter((o) => new Date(o.created_at).toDateString() === today);
  const pendingPayments = orders.filter((o) => o.payment_status === "pending");
  const revenueToday = todaysOrders
    .filter((o) => o.payment_status === "paid")
    .reduce((s, o) => s + o.total, 0);
  const soldOutCount = menuItems.filter((i) => i.available === false).length;

  const filteredOrders = orders.filter((o) => {
    if (filter === "active") return !["completed", "cancelled"].includes(o.status);
    if (filter === "pending_payment") return o.payment_status === "pending";
    return true;
  });

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0C] text-[#F5F2EB]">
      {printOrder && <Receipt order={printOrder} />}
      <header className="glass-nav sticky top-0 z-40">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <img src="/assets/mark.png" alt="" className="h-9 w-9 rounded-full object-cover" />
            <div>
              <p className="font-grotesk text-sm font-semibold">Podi Sambaram — Admin</p>
              <p className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">
                {user.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              data-testid="kitchen-mode-link"
              href="/kitchen"
              className="hidden items-center gap-2 rounded-full border border-[#F57C00]/40 px-4 py-2 text-xs font-medium text-[#FF9800] transition-colors hover:bg-[#F57C00] hover:text-black sm:flex"
            >
              <ChefHat size={13} /> Kitchen Mode
            </a>
            <button
              data-testid="sound-toggle-button"
              onClick={toggleSound}
              className={`rounded-full border p-2.5 transition-colors ${
                soundOn
                  ? "border-[#F57C00]/50 text-[#FF9800]"
                  : "border-white/10 text-zinc-500 hover:text-zinc-300"
              }`}
              aria-label={soundOn ? "Mute new order alerts" : "Unmute new order alerts"}
              title={soundOn ? "New order alerts: ON" : "New order alerts: OFF"}
            >
              {soundOn ? <Bell size={15} /> : <BellOff size={15} />}
            </button>
            <button
              data-testid="admin-refresh-button"
              onClick={fetchAll}
              className="rounded-full border border-white/10 p-2.5 text-zinc-300 transition-colors hover:border-[#F57C00]/50 hover:text-[#FF9800]"
              aria-label="Refresh"
            >
              <RefreshCw size={15} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-300 transition-colors hover:border-red-400/50 hover:text-red-300"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { icon: ShoppingBag, label: "Orders Today", value: todaysOrders.length, testid: "stat-orders-today" },
            { icon: IndianRupee, label: "Revenue Today (Paid)", value: `₹${revenueToday}`, testid: "stat-revenue-today" },
            { icon: Hourglass, label: "Pending Payments", value: pendingPayments.length, testid: "stat-pending-payments" },
            { icon: UtensilsCrossed, label: "Items Sold Out", value: soldOutCount, testid: "stat-sold-out" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="card-surface rounded-2xl p-5">
              <s.icon size={18} className="text-[#F57C00]" />
              <p className="mt-3 font-mono-accent text-2xl font-bold">{s.value}</p>
              <p className="mt-1 text-xs text-zinc-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {[
            { id: "orders", label: "Orders" },
            { id: "menu", label: "Menu Manager" },
            { id: "sales", label: "Sales Report" },
            { id: "reservations", label: "Table Reservations" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-grotesk text-sm font-medium transition-all ${
                tab === t.id ? "bg-[#F57C00] text-black" : "border border-white/10 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {t.id === "sales" && <TrendingUp size={14} />}
              {t.label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <>
            <div className="mt-5 flex flex-wrap gap-2">
              {[
                { id: "all", label: "All" },
                { id: "active", label: "Active" },
                { id: "pending_payment", label: "Pending Payment" },
              ].map((f) => (
                <button
                  key={f.id}
                  data-testid={`admin-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full border px-4 py-1.5 text-xs transition-all ${
                    filter === f.id
                      ? "border-[#F57C00]/60 bg-[#F57C00]/10 text-[#FF9800]"
                      : "border-white/10 text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {filteredOrders.length === 0 ? (
              <p data-testid="orders-empty" className="mt-10 text-center text-sm text-zinc-500">
                No orders here yet.
              </p>
            ) : (
              <div className="mt-5 space-y-4">
                {filteredOrders.map((o) => (
                  <article
                    key={o.id}
                    data-testid={`order-card-${o.order_no}`}
                    className={`card-surface rounded-2xl p-5 ${o.status === "new" ? "order-glow" : ""}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="font-mono-accent text-lg font-bold text-[#FFB74D]">{o.order_no}</span>
                        <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-zinc-400">
                          {zoneLabel(o)}
                        </span>
                        <span
                          data-testid={`order-status-badge-${o.order_no}`}
                          className={`rounded-full border px-3 py-1 text-[11px] font-medium uppercase tracking-wide ${STATUS_COLORS[o.status]}`}
                        >
                          {o.status}
                        </span>
                      </div>
                      <span className="text-xs text-zinc-500">{fmtTime(o.created_at)}</span>
                    </div>

                    <ul className="mt-3 space-y-1 text-sm text-zinc-400">
                      {o.items.map((i) => (
                        <li key={i.name} className="flex justify-between gap-3">
                          <span>{i.qty} × {i.name}</span>
                          <span className="font-mono-accent text-zinc-300">₹{i.price * i.qty}</span>
                        </li>
                      ))}
                      {o.delivery_fee > 0 && (
                        <li className="flex justify-between gap-3 text-amber-300">
                          <span>Delivery fee (beyond 3 KM)</span>
                          <span className="font-mono-accent">₹{o.delivery_fee}</span>
                        </li>
                      )}
                    </ul>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-white/8 pt-3">
                      <div className="text-xs text-zinc-500">
                        <span className="text-zinc-300">{o.customer_name}</span> • {o.phone}
                        {o.notes && <span className="mt-0.5 block italic">"{o.notes}"</span>}
                      </div>
                      <span data-testid={`order-total-${o.order_no}`} className="font-mono-accent text-lg font-bold text-[#FFB74D]">
                        ₹{o.total}
                      </span>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      {o.payment_status === "paid" ? (
                        <span
                          data-testid={`payment-badge-${o.order_no}`}
                          className="rounded-full border border-[#4CAF50]/50 bg-[#388E3C]/15 px-4 py-2 font-mono-accent text-xs font-semibold uppercase tracking-wider text-[#81C784]"
                        >
                          Paid
                        </span>
                      ) : (
                        <button
                          data-testid={`mark-paid-${o.order_no}`}
                          onClick={() => markPaid(o)}
                          className="rounded-full bg-[#388E3C] px-5 py-2 font-grotesk text-xs font-semibold text-white transition-all hover:bg-[#4CAF50]"
                        >
                          Mark Payment Received
                        </button>
                      )}
                      <select
                        data-testid={`order-status-select-${o.order_no}`}
                        value={o.status}
                        onChange={(e) => setStatus(o, e.target.value)}
                        className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-zinc-200 outline-none [color-scheme:dark]"
                      >
                        {["new", "preparing", "ready", "completed", "cancelled"].map((s) => (
                          <option key={s} value={s} className="bg-[#1A1A1E]">
                            {s[0].toUpperCase() + s.slice(1)}
                          </option>
                        ))}
                      </select>
                      <button
                        data-testid={`print-bill-${o.order_no}`}
                        onClick={() => printBill(o)}
                        className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-grotesk text-xs font-medium text-zinc-300 transition-all hover:border-[#F57C00]/50 hover:text-[#FF9800]"
                      >
                        <Printer size={13} /> Print Bill
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "menu" && (
          <MenuManager
            headers={headers}
            items={menuItems}
            categories={menuCategories}
            refresh={fetchMenu}
          />
        )}

        {tab === "sales" && (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="card-surface rounded-2xl p-6">
              <h3 className="font-grotesk text-lg font-semibold">Bestsellers</h3>
              <p className="mt-1 text-xs text-zinc-500">Item-wise quantities across all non-cancelled orders</p>
              {sales.items.length === 0 ? (
                <p data-testid="sales-empty" className="mt-6 text-sm text-zinc-500">No sales yet.</p>
              ) : (
                <table data-testid="sales-bestsellers-table" className="mt-4 w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/8 text-left font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">
                      <th className="pb-2 pr-2">#</th>
                      <th className="pb-2">Item</th>
                      <th className="pb-2 text-right">Qty Sold</th>
                      <th className="pb-2 text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.items.slice(0, 12).map((it, idx) => (
                      <tr key={it.name} className="border-b border-white/5 text-zinc-300">
                        <td className="py-2 pr-2 font-mono-accent text-xs text-zinc-500">{idx + 1}</td>
                        <td className="py-2">{it.name}</td>
                        <td className="py-2 text-right font-mono-accent">{it.qty}</td>
                        <td className="py-2 text-right font-mono-accent text-[#FFB74D]">₹{it.revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="card-surface rounded-2xl p-6">
              <h3 className="font-grotesk text-lg font-semibold">Daily Totals</h3>
              <p className="mt-1 text-xs text-zinc-500">Last 7 days — revenue counts paid orders only</p>
              {sales.days.length === 0 ? (
                <p className="mt-6 text-sm text-zinc-500">No sales yet.</p>
              ) : (
                <table data-testid="sales-daily-table" className="mt-4 w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/8 text-left font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">
                      <th className="pb-2">Day</th>
                      <th className="pb-2 text-right">Orders</th>
                      <th className="pb-2 text-right">Paid</th>
                      <th className="pb-2 text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.days.map((d) => (
                      <tr key={d.day} className="border-b border-white/5 text-zinc-300">
                        <td className="py-2">{fmtDay(d.day)}</td>
                        <td className="py-2 text-right font-mono-accent">{d.orders}</td>
                        <td className="py-2 text-right font-mono-accent">{d.paidOrders}</td>
                        <td className="py-2 text-right font-mono-accent text-[#FFB74D]">₹{d.revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === "reservations" && (
          <div className="mt-5 space-y-4">
            {reservations.length === 0 ? (
              <p data-testid="reservations-empty" className="mt-10 text-center text-sm text-zinc-500">
                No table requests yet.
              </p>
            ) : (
              [...reservations].reverse().map((r) => (
                <article key={r.id} data-testid={`reservation-card-${r.id.slice(0, 8)}`} className="card-surface rounded-2xl p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-grotesk text-base font-semibold text-zinc-100">
                      {r.name} <span className="ml-2 text-sm font-normal text-zinc-500">{r.phone}</span>
                    </p>
                    <span className="rounded-full border border-[#F57C00]/40 bg-[#F57C00]/10 px-3 py-1 font-mono-accent text-xs text-[#FFB74D]">
                      {r.date} • {r.time}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-zinc-400">
                    {r.guests} guests {r.notes && <span className="italic">— "{r.notes}"</span>}
                  </p>
                </article>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
